import React, { useState, useRef, useEffect } from 'react';
import styles from './TimelineSection.module.css';
import image1 from './images/edit menu.svg';
import image2 from './images/edit menu (1).svg';
import image3 from './images/edit menu (2).svg';
import image4 from './images/edit menu (3).svg';
import image5 from './images/edit menu (4).svg';

import {
    OptimizedMotionH2,
    OptimizedMotionP,
    OptimizedMotionImg,
} from '../../../shared/ui/optimized-motion/OptimizedMotion';
import StartButton from '../shared/start-button/StartButton';

const TimelineSection: React.FC = () => {
    const [activeTab, setActiveTab] = useState(1);
    const tabsRowRef = useRef<HTMLDivElement>(null);
    const activeTabRef = useRef<HTMLButtonElement>(null);

    const handleTabClick = (tabNumber: number) => {
        setActiveTab(tabNumber);
    };

    // Auto-scroll to active tab on mobile
    useEffect(() => {
        if (activeTabRef.current && tabsRowRef.current && window.innerWidth <= 900) {
            const tabElement = activeTabRef.current;
            const containerElement = tabsRowRef.current;

            const tabLeft = tabElement.offsetLeft;
            const tabWidth = tabElement.offsetWidth;
            const containerWidth = containerElement.offsetWidth;
            const scrollLeft = containerElement.scrollLeft;

            // Calculate optimal scroll position to center the active tab
            const targetScroll = tabLeft - containerWidth / 2 + tabWidth / 2;

            containerElement.scrollTo({
                left: targetScroll,
                behavior: 'smooth',
            });
        }
    }, [activeTab]);

    const tabTitles = [
        'Magic Editor',
        'Branded Text',
        'Instant Showcase',
        'Smart Calendar',
        'Brand Analytics',
    ];

    const images = [image1, image2, image3, image4, image5];

    const tabContent = [
        {
            title: 'Magic Editor',
            description:
                'Transform your raw product shots into premium, on-brand visuals — AI-enhanced lighting, colors, and background removal in seconds.',
        },
        {
            title: 'Branded Text',
            description:
                'Shape every sentence to echo your brand — dial tone, infuse keywords, and polish structure in seconds. One workspace that refines and harmonizes your copy.',
        },
        {
            title: 'InsightHub',
            description:
                'Your command center for content performance: monitor tone, sentiment, and trends with AI-driven insights in one real-time view.',
        },
        {
            title: 'Smart Calendar',
            description:
                'Drag-and-drop your posts onto an AI-assisted calendar that auto-suggests best publish times, flags tone gaps, and keeps every channel in sync.',
        },
        {
            title: 'Brand Analytics',
            description:
                'Track tone, consistency, and engagement in real time — see instantly which campaigns stay on-brand and which drift.',
        },
    ];

    return (
        <section className={styles.container}>
            <div className={styles.textCenter}>
                <h2 className={styles.toolboxHeading}>
                    <span>Features that make</span> <br />
                    SimpleeCreate unstoppable
                </h2>
            </div>

            <div className={styles.tabsWrapper}>
                {/* Tabs */}
                <div ref={tabsRowRef} className={styles.tabsRow}>
                    {tabTitles.map((title, index) => (
                        <button
                            key={index}
                            ref={activeTab === index + 1 ? activeTabRef : null}
                            className={`${styles.tabBtn} ${
                                activeTab === index + 1 ? styles.tabBtnActive : ''
                            }`}
                            onClick={() => handleTabClick(index + 1)}
                        >
                            {title}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className={styles.tabContentRow}>
                    <div className={styles.leftContent}>
                        <div>
                            <OptimizedMotionH2
                                className={styles.title}
                                type="slide-right"
                                delay={0.3}
                            >
                                {tabContent[activeTab - 1].title}
                            </OptimizedMotionH2>

                            <OptimizedMotionP
                                className={styles.description}
                                type="slide-left"
                                delay={0.6}
                            >
                                {tabContent[activeTab - 1].description}
                            </OptimizedMotionP>
                        </div>

                        <StartButton className={styles.button} />
                    </div>

                    <div className={styles.rightContent}>
                        <OptimizedMotionImg
                            src={images[activeTab - 1]}
                            alt={tabContent[activeTab - 1].title}
                            className={styles.image}
                            type="slide-right"
                            delay={0.4}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default TimelineSection;
